import { ApiRequestError } from "@admin/lib/api";

export type PollBackoff = {
  /** Multiplier applied per consecutive failure. Default 2. */
  factor?: number;
  /** Cap for backoff delay in ms. Default 60_000. */
  maxMs?: number;
};

export type ComputePollDelayInput = {
  intervalMs: number;
  /** Consecutive failures since last success. 0 = healthy cadence. */
  failureCount: number;
  backoffFactor?: number;
  backoffMaxMs?: number;
  /** Fraction of base delay used as +/- jitter. Default 0.2. */
  jitterRatio?: number;
  random?: () => number;
};

/** Compute next poll delay with optional exponential backoff and symmetric jitter. */
export function computePollDelay(input: ComputePollDelayInput): number {
  const backoffFactor = input.backoffFactor ?? 2;
  const backoffMaxMs = input.backoffMaxMs ?? 60_000;
  const jitterRatio = input.jitterRatio ?? 0.2;
  const random = input.random ?? Math.random;

  const base =
    input.failureCount <= 0
      ? input.intervalMs
      : Math.min(
          backoffMaxMs,
          input.intervalMs * Math.pow(backoffFactor, input.failureCount),
        );

  if (jitterRatio <= 0) return Math.max(0, base);

  const jitter = base * jitterRatio;
  return Math.max(0, base + (random() * 2 - 1) * jitter);
}

/** True for HTTP 5xx from the typed API client. */
export function isServerError(err: unknown): boolean {
  return err instanceof ApiRequestError && err.status >= 500;
}
